<script>
	/**
	 * [경제] 탭 — 재화와 매일 보상.
	 * 설계: project-le868/launcher-page-design.md §6-3 · §6-4
	 *
	 * ⚠️ 이 탭이 만지는 건 서버 ECONOMY 칸 하나다.
	 * ⚠️⚠️ **적립 금액은 저작 설정에만 산다.** 런타임이 금액을 서버로 보내는 길은 없다 —
	 *    /api/Launcher/* 가 무인증 공개 경로라, 클라이언트가 금액을 정하면 재화를 찍어낼 수 있다.
	 *    서버 claimDaily 가 여기 저장된 값을 직접 읽는다.
	 */
	import { createEventDispatcher, onMount } from "svelte";
	import { Section, Input, Textarea, Label, Select, Button, Switch } from "../../lib/ui";
	import { PRConnect } from "@projectroom/ui-lib";
	import ImagePicker from "../chat-builder/ImagePicker.svelte";
	import { botAssetFolder } from "../chat-builder/imageAssetApi.js";

	export let bot;
	/** 서버 ECONOMY 칸. { currencyLabel, currencyAssetId, dailyReward:{amount,text} } */
	export let economy = {};
	/** assetId → URL. 썸네일용(저장하지 않는다). */
	export let currencyUrl = "";

	const dispatch = createEventDispatcher();

	/**
	 * 적립 **기준** — 무엇을 세어 재화로 바꾸나.
	 * ⚠️ 서버 util/launcherEarn.js 의 `rule.basis` 와 같은 값이어야 한다.
	 * ⚠️ 기본은 'score' — 기존에 저작된 규칙엔 이 칸이 없다(없으면 점수로 눕는다, 마이그레이션 0).
	 */
	const EARN_BASIS = [
		{ value: "score", label: "점수" },
		{ value: "coins", label: "게임 코인" },
	];

	$: nEconomy = {
		...(economy || {}),
		currencyLabel: economy?.currencyLabel ?? "",
		earnRules: economy?.earnRules ?? [],
		dailyReward: {
			amount: economy?.dailyReward?.amount ?? 0,
			text: economy?.dailyReward?.text ?? "",
			...(economy?.dailyReward || {}),
		},
	};

	const emit = (patch) => dispatch("change", { ...nEconomy, ...patch });
	/* ── 적립 규칙 ──────────────────────────────────────────────────────────
	 * ⭐ 모듈(게임)은 "이런 일이 있었다"(점수)만 보내고 **얼마를 줄지는 런처가 정한다.**
	 *    모듈이 금액을 보내게 하면 클라이언트가 재화를 찍어낼 수 있다(둘 다 공개 경로다).
	 * ⚠️ 그래도 점수 자체가 클라이언트 값이라 위조를 막을 수는 없다 — **하루 상한**은
	 *    막는 장치가 아니라 **피해를 유한하게** 만드는 장치다. */
	let games = [];
	onMount(() => {
		PRConnect.send("/api/Bot/getBotListById", {}, (data) => {
			if (data.header.status !== "success") return;
			games = (data.result || []).filter(
				(b) => b.id !== bot.id && String(b.pageType || "").toUpperCase() === "GAME",
			);
		});
	});

	$: rules = Array.isArray(nEconomy.earnRules) ? nEconomy.earnRules : [];
	const setRules = (next) => emit({ earnRules: next });
	const addRule = () =>
		setRules([...rules, { from: "", basis: "score", per: 100, amount: 1, dailyCap: 30, sharedWallet: false }]);
	const patchRule = (i, fields) => setRules(rules.map((r, n) => (n === i ? { ...r, ...fields } : r)));
	const removeRule = (i) => setRules(rules.filter((_, n) => n !== i));

	const setReward = (key, value) =>
		emit({ dailyReward: { ...nEconomy.dailyReward, [key]: value } });

	let pickerOpen = false;
	const onPick = (e) => {
		const { assetId, url } = e.detail || {};
		currencyUrl = url;
		emit({ currencyAssetId: assetId });
	};
	const clearIcon = () => {
		currencyUrl = "";
		emit({ currencyAssetId: null });
	};
</script>

<div class="tw-flex tw-flex-col tw-gap-6">
	<Section title="재화" description="사용자가 모으고 쓰는 것입니다. 안 쓸 거면 비워 두세요.">
		<div class="tw-flex tw-flex-col tw-gap-4">
			<div class="tw-flex tw-items-center tw-gap-3">
				<Label class="tw-w-24 tw-shrink-0 tw-text-sm">이름</Label>
				<Input
					class="tw-flex-1"
					placeholder="예) 별소금 · 코인 · 포인트"
					value={nEconomy.currencyLabel}
					on:input={(e) => emit({ currencyLabel: e.target.value })}
				/>
			</div>
			<div class="tw-flex tw-items-start tw-gap-3">
				<Label class="tw-w-24 tw-shrink-0 tw-pt-2 tw-text-sm">그림</Label>
				<div class="tw-flex tw-items-center tw-gap-3">
					<button type="button" class="coin-thumb" on:click={() => (pickerOpen = true)} title="재화 그림 고르기">
						{#if currencyUrl}
							<img src={currencyUrl} alt="" />
						{:else}
							<span class="tw-text-[10px] tw-text-muted-foreground">고르기</span>
						{/if}
					</button>
					{#if nEconomy.currencyAssetId}
						<button type="button" class="tw-text-xs tw-text-muted-foreground tw-underline" on:click={clearIcon}>
							비우기
						</button>
					{/if}
				</div>
			</div>
			<p class="tw-text-xs tw-text-muted-foreground">
				화면 왼쪽 위 보유량 표시에 쓰입니다. 비우면 숫자만 나옵니다.
			</p>
		</div>
	</Section>

	<Section
		title="매일 접속 보상"
		description="그날 처음 들어온 사람에게 한 번 줍니다. 0이면 주지 않습니다."
	>
		<div class="tw-flex tw-flex-col tw-gap-3">
			<div class="tw-flex tw-items-center tw-gap-3">
				<Label class="tw-w-24 tw-shrink-0 tw-text-sm">주는 양</Label>
				<Input
					class="tw-w-28"
					type="number"
					min="0"
					value={nEconomy.dailyReward.amount}
					on:input={(e) => setReward("amount", Number(e.target.value) || 0)}
				/>
			</div>
			<div class="tw-flex tw-items-start tw-gap-3">
				<Label class="tw-w-24 tw-shrink-0 tw-pt-2 tw-text-sm">멘트</Label>
				<Textarea
					class="tw-flex-1"
					rows="2"
					placeholder="오늘도 놀러와줘서 고마워! 반짝반짝 별소금을 줄게."
					value={nEconomy.dailyReward.text}
					on:input={(e) => setReward("text", e.target.value)}
				/>
			</div>
			<p class="tw-text-xs tw-text-muted-foreground">
				하루 한 번은 <strong>서버가</strong> 셉니다(한국 시간 자정 기준). 새로고침해도 다시 주지 않습니다.
			</p>
		</div>
	</Section>

	<Section
		title="게임에서 벌기"
		description="게임을 하면 런처 재화가 쌓입니다. 게임 안의 코인은 그대로 두고, 이 규칙만 둘을 잇습니다."
	>
		<div class="tw-flex tw-flex-col tw-gap-2">
			{#if rules.length === 0}
				<p class="tw-text-sm tw-text-muted-foreground">아직 규칙이 없습니다. 게임을 해도 재화가 쌓이지 않습니다.</p>
			{/if}
			{#each rules as r, i (i)}
				<div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded tw-border tw-border-border tw-p-2">
					<Select class="tw-w-44" value={r.from || ""} on:change={(e) => patchRule(i, { from: e.target.value })}>
						<option value="">— 게임 고르기 —</option>
						{#each games as g (g.id)}
							<option value={g.id}>{g.title || g.id}</option>
						{/each}
					</Select>
					<!-- ⭐ **무엇을 세어 적립하나.** 점수는 "잘할수록", 코인은 "게임에서 집은 만큼"이다.
					     코인 기준 + 1당 1개로 두면 화면에서 재화를 줍는 체감이 된다. -->
					<Select
						class="tw-w-32"
						value={r.basis || "score"}
						on:change={(e) => patchRule(i, { basis: e.target.value })}
					>
						{#each EARN_BASIS as b (b.value)}
							<option value={b.value}>{b.label}</option>
						{/each}
					</Select>
					<Input
						class="tw-w-20"
						type="number"
						min="1"
						value={r.per ?? 100}
						on:input={(e) => patchRule(i, { per: Math.max(1, Number(e.target.value) || 1) })}
					/>
					<span class="tw-text-xs tw-text-muted-foreground">당</span>
					<Input
						class="tw-w-20"
						type="number"
						min="0"
						value={r.amount ?? 1}
						on:input={(e) => patchRule(i, { amount: Math.max(0, Number(e.target.value) || 0) })}
					/>
					<span class="tw-text-xs tw-text-muted-foreground">{nEconomy.currencyLabel || "재화"} · 하루 최대</span>
					<Input
						class="tw-w-20"
						type="number"
						min="0"
						value={r.dailyCap ?? 30}
						on:input={(e) => patchRule(i, { dailyCap: Math.max(0, Number(e.target.value) || 0) })}
					/>
					<Button variant="ghost" size="sm" on:click={() => removeRule(i)}>삭제</Button>

					<!-- ⭐⭐ **지갑 통합.** 적립 기준(basis)과 **다른 결정**이라 따로 둔다 —
					     점수로 벌면서 지갑으로 쓰는 조합도 있을 수 있다. -->
					<div class="tw-flex tw-w-full tw-items-center tw-gap-2 tw-border-t tw-border-border tw-pt-2 tw-text-sm">
						<Switch
							checked={r.sharedWallet === true}
							on:click={() => patchRule(i, { sharedWallet: !(r.sharedWallet === true) })}
							aria-label="게임에서도 런처 재화를 쓴다"
						/>
						게임에서도 {nEconomy.currencyLabel || "런처 재화"}를 쓴다 (지갑 통합)
					</div>
					{#if r.sharedWallet === true}
						<p class="tw-w-full tw-text-xs tw-text-muted-foreground">
							게임의 코인 표시·캐릭터/맵 해금이 <strong>런처 지갑</strong>을 씁니다 — 레시피에 쓰면
							게임에서도 줄어듭니다. 게임 안에 따로 쌓이던 코인은 쓰지 않습니다.
							<br />⚠️ <strong>게임 빌더의 '재화 이름'</strong>도 "{nEconomy.currencyLabel || "재화"}"로
							맞춰 주세요 — 숫자는 같은데 이름만 다르면 더 헷갈립니다.
							<br />⚠️ 런처를 거치지 않고 게임에 바로 들어온 사람은 예전처럼 게임 코인을 씁니다.
						</p>
					{/if}
				</div>
			{/each}
			<div>
				<Button variant="outline" size="sm" on:click={addRule}>+ 규칙 추가</Button>
			</div>
			{#if rules.some((r) => r?.basis === "coins")}
				<p class="tw-text-xs tw-text-muted-foreground">
					⚠️ <strong>게임 코인</strong> 기준이어도 게임 안 코인은 그대로 쌓입니다(캐릭터·맵 해금에 씁니다)
					— 재화를 옮기는 게 아니라 <strong>같은 획득을 둘이 각자 셉니다</strong>. 게임에서 집은 것이
					곧 {nEconomy.currencyLabel || "재화"}가 되게 하려면 <strong>1당 1개</strong>로 두세요.
				</p>
			{/if}
			<p class="tw-text-xs tw-text-muted-foreground">
				⚠️ <strong>하루 최대</strong>를 꼭 정하세요. 점수·코인 모두 게임 화면에서 올라오는 값이라 위조를
				완전히 막을 수 없습니다 — 상한이 피해를 그날 그만큼으로 묶어 둡니다. 0 이면 상한이 없습니다.
			</p>
		</div>
	</Section>
</div>

<ImagePicker
	bind:open={pickerOpen}
	chatId={bot.id}
	contextFolder={botAssetFolder(bot)}
	mode="single"
	currentAssetId={nEconomy.currencyAssetId || null}
	on:select={onPick}
/>

<style>
	.coin-thumb {
		width: 56px;
		height: 56px;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		border-radius: 10px;
		border: 1px dashed hsl(var(--sb-border));
		background: hsl(var(--sb-muted));
		cursor: pointer;
	}
	.coin-thumb img {
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
	}
</style>
